import { ComponentProps, useContext } from 'react';
import { ShareInfo } from '@pelckmans/business-components/types/Audiences';
import orderBy from 'lodash/orderBy';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import useAnalytics from '../../../../../hooks/useAnalytics';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import ModalContext from '../../../../../contexts/ModalContext';
import useLtiUserMaterial from './useLtiUserMaterial';
import useLinkUserMaterial from './useLinkUserMaterial';
import useFileUserMaterial from './useFileUserMaterial';
import {
  deleteUserMaterial,
  isFile,
  isLink,
  isLti,
  isScorableUserMaterial,
  isSharedUserMaterialAssignment,
  UserMaterial,
} from '../../../../../api/user-material';
import { ValuesOfObject } from '../../../../../@types/utils';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
// eslint-disable-next-line import/no-restricted-paths
import Score from '../../../../../modules/material/_components/common/Score';
// eslint-disable-next-line import/no-restricted-paths
import { ID_NOTES_LINK } from '../../../../../modules/material/_hooks/useFilteredMaterials';
import MaterialAssignmentModal from '../../../../../shared-components/elementis/components/material-assignment';

export default function useUserMaterial() {
  const handleError = useThrowAsyncError();
  const analytics = useAnalytics();

  const { selectedNode } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);
  const { userMaterial, setUserMaterial, fetchUserMaterial } = useContext(UserMaterialContext);

  const { openLtiUserMaterial } = useLtiUserMaterial();
  const { openLinkUserMaterial } = useLinkUserMaterial();
  const { openFileUserMaterial } = useFileUserMaterial();

  const addUserMaterial = (material: UserMaterial) => {
    const targetIndex = userMaterial.find(x => x.id === ID_NOTES_LINK) ? 1 : 0;

    setUserMaterial(userMaterial => [
      ...userMaterial.slice(0, targetIndex),
      material,
      ...userMaterial.slice(targetIndex),
    ]);
  };

  const removeUserMaterial = async (material: UserMaterial, nodeId?: string) => {
    const materialNodeId = selectedNode?.id || nodeId;

    if (!materialNodeId) return;

    try {
      await deleteUserMaterial(material.moduleId, materialNodeId, material.id);
      setUserMaterial(userMaterial.filter(item => item.id !== material.id));
    } catch (e) {
      handleError(e);
    }
  };

  const shareUserMaterial = (materialId: string, shareInfo?: ShareInfo) => {
    const shared = !!shareInfo?.audiences?.find(({ assignment }) => !assignment);
    const assignment = shareInfo?.audiences?.find(({ assignment }) => assignment)?.assignment;

    setUserMaterial(userMaterial =>
      userMaterial.map(current => {
        if (current.id !== materialId) return current;
        return { ...current, shared, assignment } as UserMaterial;
      }),
    );
  };

  const updateUserMaterial = (newMaterial: UserMaterial) => {
    setUserMaterial(userMaterial => {
      const updateUserMaterials = userMaterial.map(current => {
        if (current.id !== newMaterial.id) return current;
        return { ...current, ...newMaterial };
      });

      return orderBy(updateUserMaterials, ['lastUpdatedAt'], ['desc']);
    });
  };

  const openFreeExerciseOrAssignment = async (
    material: UserMaterial,
    onAssignmentClosed?: () => Promise<void>,
    subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
  ) => {
    if (isSharedUserMaterialAssignment(material) && !isScorableUserMaterial(material)) {
      const materialAssignment: ComponentProps<typeof MaterialAssignmentModal>['material'] = {
        ...material,
        type: 'user-material',
      };

      const handleOpenMaterial = async () => {
        const modal = await openMaterial(material, undefined, subLocationId);

        if (modal) {
          return showModal(modal);
        }
      };

      const handleModalClosed = () => {
        if (onAssignmentClosed) onAssignmentClosed();
      };

      return (
        <MaterialAssignmentModal
          id="material-assignment-modal"
          material={materialAssignment}
          onOpenMaterial={handleOpenMaterial}
          onModalClosed={handleModalClosed}
        />
      );
    }

    return openMaterial(material, onAssignmentClosed, subLocationId);
  };

  const openMaterial = async (
    material: UserMaterial,
    onModalClosed?: () => Promise<void>,
    subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
  ) => {
    if (subLocationId) {
      await analytics.capture({
        eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_ACCESSED,
        subLocationId,
        objectId: material.id,
      });
    }

    if (isLink(material)) {
      return openLinkUserMaterial(material);
    }

    if (isFile(material)) {
      return openFileUserMaterial(material);
    }

    if (isLti(material)) {
      return openLtiUserMaterial(material, onModalClosed);
    }
  };

  const getScore = (userMaterial: UserMaterial) => {
    if (!userMaterial.userMaxScore || !isLti(userMaterial)) return null;

    return <Score score={userMaterial.score} needsGrading={userMaterial.needsGrading} />;
  };

  return {
    userMaterial,
    addUserMaterial,
    removeUserMaterial,
    shareUserMaterial,
    updateUserMaterial,
    openMaterial: openFreeExerciseOrAssignment,
    getScore,
    fetchUserMaterial,
  };
}
